import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { getEnabledMenuItems, isAdminGateOpen } from '../utilities';
import { ADMIN_MANIFEST } from './adminSections';
import { AdminShowAdvancedContext } from './AdminSettingsContext';
import AdminTabContent from './components/AdminTabContent';
import { PageTitle } from '../../components/StyledComponents';

// D.4 Admin landing. Renders a three-tab header (Platform / Program / Project)
// and delegates to AdminTabContent for the active tab's accordion tree.
//
// URL model:
//   /admin           → redirects to the first tab the user can access
//   /admin/platform  → Platform Settings tab
//   /admin/program   → Program Settings tab
//   /admin/project   → Project Settings tab
//
// Accordion state (which sections/items are expanded) is local component state
// — not reflected in the URL for D.4. Deep-linking to a specific section is a
// future enhancement.

const AdminLanding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // When on, items flagged `advanced: true` in the manifest (join/link
  // tables, raw audit logs) become visible. Off by default to keep the
  // page task-oriented.
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { actions, availableForms } = useSelector((state: any) => state.user);
  const enabledItems = useMemo(
    () => getEnabledMenuItems(actions, availableForms),
    [actions, availableForms],
  );

  // Determine accessible tabs. Tab is accessible if the user has sitewide access
  // OR the permission for any section within the tab resolves via isAdminGateOpen.
  // The Advanced tab is additionally gated on the "Show advanced" toggle.
  const accessibleTabs = useMemo(
    () =>
      ADMIN_MANIFEST.filter((tab) => {
        if (tab.id === 'advanced' && !showAdvanced) return false;
        // Empty-sections tabs (e.g., Project Settings placeholder) are
        // always shown so the placeholder copy renders.
        if (tab.sections.length === 0) return true;
        return tab.sections.some((section) =>
          isAdminGateOpen(enabledItems, section.permissionKey),
        );
      }),
    [enabledItems, showAdvanced],
  );

  const activeTab = useMemo(
    () => ADMIN_MANIFEST.find((t) => t.path === location.pathname),
    [location.pathname],
  );

  // Keep document.title in sync with the active tab.
  useEffect(() => {
    if (activeTab) {
      document.title = `Admin · ${activeTab.label} — Athena`;
    } else {
      document.title = 'Admin — Athena';
    }
    return () => {
      document.title = 'Athena';
    };
  }, [activeTab]);

  // Bare /admin — redirect to first accessible tab, or fall through to the
  // access-denied message below.
  if (location.pathname === '/admin' && accessibleTabs.length > 0) {
    return <Navigate to={accessibleTabs[0].path} replace />;
  }

  if (accessibleTabs.length === 0) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ maxWidth: 560 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/')}>
              Home
            </Button>
          }
        >
          <Typography variant="subtitle2" gutterBottom>
            No admin access
          </Typography>
          Your account does not currently have any administrative permissions.
          If you believe this is an error, contact a platform administrator.
        </Alert>
      </Box>
    );
  }

  // Unknown admin path — redirect to the first accessible tab.
  if (!activeTab) {
    return <Navigate to={accessibleTabs[0].path} replace />;
  }

  const handleChangeTab = (_: React.SyntheticEvent, newPath: string) => {
    navigate(newPath);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        p: 3,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ pb: 3 }}
      >
        <Stack spacing={0.5}>
          <PageTitle>Administrator Settings</PageTitle>
          {/* Subtitle removed; placeholder preserves the vertical rhythm. */}
          <Typography variant="body2">&nbsp;</Typography>
        </Stack>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showAdvanced}
              onChange={(e) => setShowAdvanced(e.target.checked)}
            />
          }
          label="Show advanced"
          labelPlacement="start"
          sx={{ ml: 0, color: 'text.secondary' }}
        />
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab.path}
          onChange={handleChangeTab}
          aria-label="Admin scope tabs"
        >
          {accessibleTabs.map((tab) => (
            <Tab
              key={tab.id}
              label={tab.label}
              value={tab.path}
              id={`admin-scope-tab-${tab.id}`}
              aria-controls={`admin-scope-tabpanel-${tab.id}`}
            />
          ))}
        </Tabs>
      </Box>

      <Box
        role="tabpanel"
        id={`admin-scope-tabpanel-${activeTab.id}`}
        aria-labelledby={`admin-scope-tab-${activeTab.id}`}
        sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
      >
        <AdminShowAdvancedContext.Provider value={showAdvanced}>
          <AdminTabContent
            // Key by tab id so the per-tab default-expand rules in
            // AdminTabContent's useState initializer re-run on every tab switch
            // instead of carrying state over from the first tab the user landed on.
            key={activeTab.id}
            tab={activeTab}
            enabledItems={enabledItems}
            showAdvanced={showAdvanced}
          />
        </AdminShowAdvancedContext.Provider>
      </Box>
    </Box>
  );
};

export default AdminLanding;
